"use client";
import { EmptyQueryBoundary, EmptyQueryHits } from "./EmptyQuery";
import ProductListItem from "./ProductListItem";
import { useProductSearchQuery } from "./ProductSearchContextProvider";
import useAutocomplete from "./hooks/useAutocomplete";
import {
  Suggestions,
  SearchResultWithHighlight,
  ProductSummaryWithHighlight,
} from "./types";
import styles from "./index.module.css";
import { sendAnalyticsProps } from "./index";
import { PRODUCT_INDEX_EN } from "@/constants/algolia";
import useMarketplaceAttributionHref from "@/hooks/useMarketplaceAttributionHref";

interface ProductSearchSuggestionsProps {
  suggestions: Suggestions;
  sendAnalytics: (props: sendAnalyticsProps) => void;
  location?: string;
}

export const getAllResultsHref = (query: string) => {
  const params = new URLSearchParams();
  if (query.trim()) {
    params.set("q", query.trim());
  }
  const queryString = params.toString();
  return `/search${queryString ? `?${queryString}` : ""}`;
};

function ProductSearchSuggestions({
  suggestions,
  sendAnalytics,
  location,
}: ProductSearchSuggestionsProps) {
  const { query } = useProductSearchQuery();
  const results = useAutocomplete(query);
  const isProductIndex = PRODUCT_INDEX_EN === "product";
  const allResultsHref = useMarketplaceAttributionHref(
    getAllResultsHref(query),
  );

  const getUuid = (
    result: SearchResultWithHighlight | ProductSummaryWithHighlight,
  ) => {
    return isProductIndex
      ? (result as SearchResultWithHighlight).uuid
      : (result as ProductSummaryWithHighlight).productUuid;
  };

  return (
    <EmptyQueryBoundary
      fallback={
        <EmptyQueryHits suggestions={suggestions} location={location} />
      }
      query={query}
      results={results}
    >
      <div className="bg-white rounded shadow-md overflow-hidden">
        <ul className="list-none p-0 my-0">
          {results.map((result) => (
            <li
              key={getUuid(result)}
              className={`${styles.searchListItem} m-0 px-3 py-2`}
            >
              <ProductListItem hit={result} location={location} />
            </li>
          ))}
        </ul>
        <div className="pl-3 py-2 bg-light">
          <a
            onClick={() => {
              sendAnalytics({ fromViewAllButton: true });
            }}
            href={allResultsHref}
          >
            View all results
          </a>
        </div>
      </div>
    </EmptyQueryBoundary>
  );
}

export default ProductSearchSuggestions;
